import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

interface State {
  show: boolean;
  labelConfirm: string;
  onConfirm: () => void;
  title?: string;
  message: string;
}

const defaultState: State = {
  show: false,
  labelConfirm: 'Ok',
  onConfirm: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  message: '',
};

export const SystemDialogContext = createContext<{
  state: State;
  open: (arg: Omit<State, 'show'>) => void;
  close: () => void;
}>({
  state: defaultState,
  open: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  close: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
});

export const useShowSystemApprove = (): ((
  message: string,
  action: () => void,
  label: string,
  title?: string,
) => void) => {
  const { open, close } = useContext(SystemDialogContext);

  return useCallback(
    (message, action, label, title) =>
      open({
        labelConfirm: label,
        message,
        title,
        onConfirm: () => {
          action();
          close();
        },
      }),
    [open, close],
  );
};

export const SystemDialogContextProvider = ({ children }: { children: ReactNode }) => {
  const [value, setValue] = useState(defaultState);

  return (
    <SystemDialogContext.Provider
      value={useMemo(
        () => ({
          state: value,
          open: arg => setValue({ ...arg, show: true }),
          close: () => setValue(defaultState),
        }),
        [value],
      )}
    >
      {children}
    </SystemDialogContext.Provider>
  );
};
